/**
 * What a dish is made of.
 *
 * WHY THIS SCREEN DID NOT EXIST AND WHY THAT MATTERED
 * --------------------------------------------------
 * A recipe could be created once, through the API, and never touched again.
 * Change a portion size, swap an ingredient, correct a quantity somebody typed
 * wrong — none of it was possible from anywhere. And a recipe is what decides
 * how much comes off the shelf when a dish is sold, and what that dish is
 * reckoned to cost.
 *
 * So every food-cost figure in the system was frozen at whatever was first
 * entered, and there was no way to unfreeze it.
 *
 * WHY THE FOOD COST IS SHOWN WHILE YOU TYPE
 * -----------------------------------------
 * Because it is the question the person editing is actually asking. "Another
 * twenty grams of cheese" is meaningless on its own; "another twenty grams of
 * cheese takes this dish from thirty per cent to thirty-eight" is a decision.
 * Made to be worked out afterwards on a report, it will not be worked out.
 *
 * The industry rule of thumb is that food cost around thirty per cent leaves
 * room for everything else. That is a rule of thumb rather than a law, so the
 * figure is coloured rather than blocked — a dish can be a loss-leader on
 * purpose, and this screen has no business refusing it.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

const field =
  'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

/**
 * How dear is dear.
 *
 * Thirty per cent is the usual mark; past forty the dish is eating the
 * margin. Kept here rather than buried in the markup so the numbers behind
 * the colours are visible and arguable.
 */
const COMFORTABLE = 30;
const DEAR = 40;

export function RecipeForm({ product, onClose, onSaved }) {
  const recipe = useQuery({
    queryKey: ['menu', 'recipe', product.id],
    queryFn: async () => {
      const { recipes } = await api.get(`/menu/recipes?product_id=${product.id}`);
      if (!recipes?.length) return null;
      return api.get(`/menu/recipes/${recipes[0].id}`);
    },
  });

  const ingredients = useQuery({
    queryKey: ['menu', 'ingredients'],
    queryFn: () => api.get('/menu/ingredients'),
  });

  const units = useQuery({
    queryKey: ['menu', 'units'],
    queryFn: () => api.get('/menu/units'),
  });

  /**
   * The lines as saved, and the lines as edited.
   *
   * Kept apart so abandoning the screen leaves the saved recipe alone, and
   * derived rather than copied into state when the query lands.
   *
   * The obvious shortcut — setting state during render once the data arrives —
   * does not work: React finishes the current render before re-running it, so
   * the markup below reads the old value and falls over on the first pass.
   * That is what happened, and it is why the tests render this component
   * rather than only testing the arithmetic.
   */
  const saved = useMemo(
    () =>
      (recipe.data?.components ?? []).map((c) => ({
        ingredientId: c.ingredient_id,
        unitId: c.unit_id,
        quantity: c.quantity,
      })),
    [recipe.data]
  );

  const [edited, setEdited] = useState(null);
  const [renamed, setRenamed] = useState(null);

  const lines = edited ?? saved;
  const name = renamed ?? recipe.data?.name ?? `${product.name} recipe`;

  const setLines = (next) =>
    setEdited(typeof next === 'function' ? next(lines) : next);
  const setName = setRenamed;

  const loaded = recipe.data !== undefined && ingredients.data && units.data;

  const byId = useMemo(() => {
    const map = new Map();
    for (const i of ingredients.data?.ingredients ?? []) map.set(i.id, i);
    return map;
  }, [ingredients.data]);

  /**
   * What it costs, worked out here rather than asked of the server.
   *
   * The server is the authority and recalculates on save; this is so the
   * figure moves as somebody types. The two agree because both multiply the
   * quantity by what the ingredient costs.
   */
  const cost = useMemo(
    () =>
      (lines ?? []).reduce((sum, line) => {
        const ingredient = byId.get(line.ingredientId);
        const each = Number(ingredient?.cost_per_unit ?? 0);
        return sum + each * (Number(line.quantity) || 0);
      }, 0),
    [lines, byId]
  );

  const price = Number(product.price) || 0;
  const share = price > 0 ? (cost / price) * 100 : null;

  const save = useMutation({
    mutationFn: () =>
      api.put(`/menu/recipes/${recipe.data.id}`, {
        name: name.trim(),
        components: lines.map((l) => ({
          ingredientId: l.ingredientId,
          unitId: l.unitId,
          quantity: Number(l.quantity),
        })),
      }),
    onSuccess: onSaved,
  });

  const usable =
    lines?.length > 0 &&
    lines.every((l) => l.ingredientId && Number(l.quantity) > 0) &&
    name.trim();

  function change(index, patch) {
    setLines((current) => current.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  if (!loaded) {
    return (
      <div className="mb-5 rounded-xl border border-rule bg-surface p-5 text-sm text-ink-3">
        Loading the recipe…
      </div>
    );
  }

  if (!recipe.data) {
    return (
      <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
        <h3 className="mb-2 font-medium text-ink">{product.name}</h3>
        {/*
          A dish with no recipe sells perfectly well and takes nothing off the
          shelf, which is why its food cost reads as nought rather than as a
          missing figure. Worth saying plainly.
        */}
        <p className="text-sm text-ink-2">
          This dish has no recipe, so selling it takes nothing off the shelf and it appears to
          cost nothing to make.
        </p>
        <div className="mt-4">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-medium text-ink">{product.name}</h3>

        {/* The figure the person editing is actually asking about. */}
        <div className="flex items-baseline gap-4 text-sm">
          <span className="text-ink-3">
            Sells for <span className="tabular-nums text-ink-2">{formatPKR(price)}</span>
          </span>
          <span className="text-ink-3">
            Costs <span className="tabular-nums text-ink">{formatPKR(cost)}</span>
          </span>
          {share === null ? null : (
            <span
              className={`tabular-nums font-medium ${
                share > DEAR ? 'text-bad' : share > COMFORTABLE ? 'text-warn' : 'text-brand-400'
              }`}
            >
              {share.toFixed(0)}% food cost
            </span>
          )}
        </div>
      </div>

      <label className="mb-4 block max-w-md">
        <span className="mb-1.5 block text-xs text-ink-3">Recipe name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
      </label>

      <div className="space-y-2">
        {lines.map((line, index) => {
          const ingredient = byId.get(line.ingredientId);
          const lineCost = Number(ingredient?.cost_per_unit ?? 0) * (Number(line.quantity) || 0);

          return (
            <div key={index} className="flex flex-wrap items-end gap-2">
              <label className="min-w-56 flex-1">
                <span className="sr-only">Ingredient</span>
                <select
                  aria-label={`Ingredient ${index + 1}`}
                  value={line.ingredientId ?? ''}
                  onChange={(e) => {
                    const picked = byId.get(e.target.value);
                    // The ingredient's own unit is nearly always the right
                    // one, and picking it saves a second decision.
                    change(index, {
                      ingredientId: e.target.value,
                      unitId: line.unitId || picked?.unit_id || '',
                    });
                  }}
                  className={field}
                >
                  <option value="">Choose an ingredient</option>
                  {(ingredients.data.ingredients ?? []).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="w-28">
                <span className="sr-only">Quantity</span>
                <input
                  aria-label={`Quantity ${index + 1}`}
                  type="number"
                  step="0.001"
                  min="0"
                  value={line.quantity}
                  onChange={(e) => change(index, { quantity: e.target.value })}
                  className={`${field} tabular-nums`}
                />
              </label>

              <label className="w-32">
                <span className="sr-only">Unit</span>
                <select
                  aria-label={`Unit ${index + 1}`}
                  value={line.unitId ?? ''}
                  onChange={(e) => change(index, { unitId: e.target.value })}
                  className={field}
                >
                  <option value="">Unit</option>
                  {(units.data.units ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.abbreviation}
                    </option>
                  ))}
                </select>
              </label>

              <span className="w-24 pb-3 text-right text-sm tabular-nums text-ink-2">
                {formatPKR(lineCost)}
              </span>

              <button
                type="button"
                onClick={() => setLines(lines.filter((_, i) => i !== index))}
                aria-label={`Remove ingredient ${index + 1}`}
                className="pb-3 text-sm text-ink-3 hover:text-bad"
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-3">
        <Button
          variant="secondary"
          onClick={() => setLines([...lines, { ingredientId: '', unitId: '', quantity: '' }])}
        >
          Add an ingredient
        </Button>
      </div>

      {save.isError && (
        <p className="mt-3 text-sm text-bad">
          {save.error?.message ?? 'That did not save.'}
        </p>
      )}

      {lines.length === 0 && (
        <p className="mt-3 text-sm text-warn">
          {/*
            Explained rather than just disabled. A greyed-out button with no
            reason beside it is the most common way a form wastes somebody's
            afternoon.
          */}
          A recipe needs at least one ingredient. Without one the dish sells, takes nothing off
          the shelf, and reports no cost at all.
        </p>
      )}

      <div className="mt-5 flex gap-2">
        <Button onClick={() => save.mutate()} disabled={!usable || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save recipe'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
